// Los productos que el agente suma a un catálogo ya armado
// (ADR-2026-10-15-el-agente-suma-productos-al-catalogo), con la MISMA forma que les da la portada:
// nombre, portada, medidas y ficha. Primero se buscan en el catálogo de la marca que el chat ya
// tiene cargado; los que no están ahí (uno recién creado con agregar_productos, antes de que el
// catálogo se refresque) se leen de la base, siempre dentro de la empresa.
import { supabase } from '../../../../lib/supabase';
import { fetchFichaPorVariante, fetchMedidasPorVariante } from '../../medidasApi';
import type { MockProductV2 } from '../../mockData';
import { fetchProductImageUrls } from '../../productsApi';
import type { ProductoParaElPlan } from './plan';

export interface FuentesDeProductos {
  /** Nombre y portada de los que no están en el catálogo cargado. */
  buscarEnBase: (ids: string[]) => Promise<{ id: string; nombre: string; portadaUrl?: string }[]>;
  medidas: typeof fetchMedidasPorVariante;
  fichas: typeof fetchFichaPorVariante;
}

export async function buscarProductosParaElPlan(
  ids: string[],
  catalogo: readonly MockProductV2[],
  fuentes: FuentesDeProductos,
): Promise<{ encontrados: ProductoParaElPlan[]; noEncontrados: string[] }> {
  const delCatalogo = new Map<string, { nombre: string; portadaUrl?: string }>(
    catalogo.filter((p) => ids.includes(p.id)).map((p) => [p.id, { nombre: p.name, portadaUrl: p.imageUrl }]),
  );
  const faltan = ids.filter((id) => !delCatalogo.has(id));
  const deLaBase = faltan.length > 0 ? await fuentes.buscarEnBase(faltan) : [];
  for (const p of deLaBase) delCatalogo.set(p.id, { nombre: p.nombre, portadaUrl: p.portadaUrl });

  const hallados = ids.filter((id) => delCatalogo.has(id));
  if (hallados.length === 0) return { encontrados: [], noEncontrados: ids };
  const [medidas, fichas] = await Promise.all([fuentes.medidas(hallados), fuentes.fichas(hallados)]);
  return {
    encontrados: hallados.map((id) => {
      const p = delCatalogo.get(id)!;
      return {
        varianteId: id,
        nombre: p.nombre,
        ...(p.portadaUrl ? { portadaUrl: p.portadaUrl } : {}),
        medidas: medidas[id] ?? null,
        ficha: fichas[id] ?? { campos: [] },
      };
    }),
    noEncontrados: ids.filter((id) => !delCatalogo.has(id)),
  };
}

/** Las fuentes reales: las variantes activas de la empresa y su primera foto. */
export function fuentesDeProductos(companyId: string): FuentesDeProductos {
  return {
    buscarEnBase: async (ids) => {
      const { data } = await supabase
        .from('variantes')
        .select('id, nombre')
        .eq('id_company', companyId)
        .eq('activo', true)
        .in('id', ids);
      const filas = (data ?? []) as { id: string; nombre: string | null }[];
      return Promise.all(
        filas.map(async (f) => {
          const fotos = await fetchProductImageUrls(companyId, f.id).catch(() => [] as string[]);
          return { id: f.id, nombre: f.nombre || 'Producto', ...(fotos[0] ? { portadaUrl: fotos[0] } : {}) };
        }),
      );
    },
    medidas: fetchMedidasPorVariante,
    fichas: fetchFichaPorVariante,
  };
}
